import { useCallback, useMemo, useRef } from 'react'
import { cn } from '@/lib/utils'
import {
  contrastingForeground,
  sanitizeOrderTypeHexColor,
  normalizeOrderTypeId,
} from '@/lib/orderTypeSettings'
import { CounterOrderTypeGlyph } from '@/components/counter/CounterOrderTypeAnimatedIcons'

export type CounterOrderTypeOption = {
  id: string
  label: string
  description?: string
  color: string
}

const motionSegment =
  'transition-[transform,box-shadow,background-color,border-color,color] duration-200 ease-out [@media(prefers-reduced-motion:reduce)]:transition-none'
const motionHoverLift =
  'hover:-translate-y-px hover:shadow-sm active:translate-y-0 [@media(prefers-reduced-motion:reduce)]:hover:translate-y-0 [@media(prefers-reduced-motion:reduce)]:hover:shadow-none'

type CounterOrderTypeToggleProps = {
  value: string
  onChange: (next: string) => void
  /** Enabled tabs (ids); normalized when matching */
  enabledIds: ReadonlySet<string>
  /** Full row metadata including labels and colors */
  options: CounterOrderTypeOption[]
}

export function CounterOrderTypeToggle({ value, onChange, enabledIds, options }: CounterOrderTypeToggleProps) {
  const groupRef = useRef<HTMLDivElement>(null)

  const visibleOptions = useMemo(() => {
    return options.filter((o) => enabledIds.has(normalizeOrderTypeId(o.id)))
  }, [options, enabledIds])

  const focusValue = useCallback((v: string) => {
    const root = groupRef.current
    if (!root) return
    const nid = normalizeOrderTypeId(v)
    const btn = root.querySelector<HTMLButtonElement>(`button[data-order-type="${nid}"]`)
    btn?.focus()
  }, [])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (visibleOptions.length === 0) return
      const idx = visibleOptions.findIndex((o) => normalizeOrderTypeId(o.id) === normalizeOrderTypeId(value))
      if (idx < 0) return
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        const next = visibleOptions[(idx + 1) % visibleOptions.length].id
        onChange(next)
        focusValue(next)
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        const next = visibleOptions[(idx - 1 + visibleOptions.length) % visibleOptions.length].id
        onChange(next)
        focusValue(next)
      } else if (e.key === 'Home') {
        e.preventDefault()
        const next = visibleOptions[0].id
        onChange(next)
        focusValue(next)
      } else if (e.key === 'End') {
        e.preventDefault()
        const next = visibleOptions[visibleOptions.length - 1].id
        onChange(next)
        focusValue(next)
      }
    },
    [focusValue, onChange, value, visibleOptions]
  )

  if (visibleOptions.length === 0) return null

  const gridStyle = useMemo(
    () => ({
      gridTemplateColumns: `repeat(${Math.min(visibleOptions.length, 6)}, minmax(0, 1fr))`,
    }),
    [visibleOptions.length]
  )

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label="Order type"
      onKeyDown={onKeyDown}
      className="@container rounded-2xl border border-border/80 bg-muted/30 p-1 shadow-inner dark:bg-muted/15"
    >
      <div className={cn('grid gap-1 @lg:gap-1.5')} style={gridStyle}>
        {visibleOptions.map((opt) => {
          const selected = normalizeOrderTypeId(value) === normalizeOrderTypeId(opt.id)
          const hex = sanitizeOrderTypeHexColor(opt.color, '#64748b')
          const fg = contrastingForeground(hex)
          const idleBg = `color-mix(in srgb, ${hex} 14%, hsl(var(--muted) / 0.35))`
          const idleBorder = `color-mix(in srgb, ${hex} 38%, hsl(var(--border)))`

          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={selected}
              title={opt.description}
              tabIndex={selected ? 0 : -1}
              data-order-type={normalizeOrderTypeId(opt.id)}
              onClick={() => onChange(opt.id)}
              className={cn(
                // Tile content adapts to the PANE width (container query), not the
                // viewport — the checkout rail can squeeze this to <400px even on
                // desktop. Narrow: icon stacked over label; wide: side by side.
                'flex min-h-[52px] @xl:min-h-[56px] min-w-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-xl border px-1 py-1.5 text-center outline-none select-none touch-manipulation @lg:flex-row @lg:gap-2 @lg:px-1.5 @lg:py-2',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                motionSegment,
                motionHoverLift,
                !selected && 'text-foreground dark:text-foreground'
              )}
              style={
                selected
                  ? {
                      backgroundColor: hex,
                      borderColor: hex,
                      color: fg,
                      boxShadow: `0 4px 14px -4px color-mix(in srgb, ${hex} 55%, transparent)`,
                    }
                  : {
                      backgroundColor: idleBg,
                      borderColor: idleBorder,
                    }
              }
            >
              <span
                className={cn(
                  'flex shrink-0 items-center justify-center',
                  selected ? 'text-current' : 'opacity-90'
                )}
                style={!selected ? { color: hex } : undefined}
              >
                <CounterOrderTypeGlyph type={opt.id} active={selected} className="h-7 w-7 @lg:h-10 @lg:w-10 @3xl:h-[48px] @3xl:w-[48px]" />
              </span>
              <span className="min-w-0 max-w-full self-center truncate text-[11.5px] @lg:text-[14.3px] @3xl:text-[17px] font-semibold leading-tight @lg:leading-none tracking-tight">{opt.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
